import React, { JSX, useState } from "react";
import { AccessControlKeys } from "utilities/enumerations/authorization/access-control-keys";
import {
    ActiveStatus,
} from "models/enumerations/active-status/active-status";
import { ButtonSize, ButtonStyle } from "components/buttons/button/button";
import { ButtonIcon } from "components/buttons/button-icon/button-icon";
import { CollectionUtils } from "andculturecode-javascript-core";
import { CourseList } from "components/courses/course-lists/course-list/course-list";
import { CourseModal } from "components/courses/course-modal/course-modal";
import { HeaderBanner } from "components/header-banner/header-banner";
import { Icons } from "components/icons/constants/icons";
import { Pager } from "components/pager/pager";
import {
    Paragraph,
    ParagraphSize,
    ParagraphStyle,
} from "components/typography/paragraph/paragraph";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { t } from "utilities/localization/t";
import { useCourses } from "utilities/hooks/models/courses/use-courses";
import { validatePageAccess } from "utilities/decorators/aspects/authorization/validate-page-access";
import { useCourseSearch } from "utilities/contexts/search/course/course-search-context";
import AdminCourseFilterComponent from "../admin-course-filter-component/admin-course-filter-component";
import "./admin-course-list-page.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface AdminCourseListPageProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "admin-course-list-page";
const ITEMS_PER_PAGE = 20;

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const AdminCourseListPage: React.FC<AdminCourseListPageProps> = validatePageAccess(
    AccessControlKeys.AdminCourseListPage
)((): JSX.Element => {
    const { initialCriteria, setCriteria } = useCourseSearch();
    const [showCourseModal, setShowCourseModal] = useState(false);
   

    const { courses, isLoading, rowCount: totalNumberOfCourses } = useCourses({
        activeStatus: initialCriteria?.status ?? ActiveStatus.Active,
        applySearch: true,
        audienceType: initialCriteria?.audienceType ?? undefined,
        includeArchived: true,
        includeCreatedBy: true,
        includeLastModifiedBy: true,
        includeLatestVersion: true,
        language: initialCriteria?.language ?? undefined,
        searchText: initialCriteria?.searchText ?? "",
        skip: ((initialCriteria?.currentPage ?? 1) - 1) * ITEMS_PER_PAGE,
        take: ITEMS_PER_PAGE,
        topic: initialCriteria?.topic ?? undefined,
    });

    const onPageClick = (pageNumber: number) => {
        setCriteria((prev) => ({ ...prev, currentPage: pageNumber }));
    };

    return (
        <>
            <HeaderBanner title={t("courses")}>
                <ButtonIcon
                    dataTestId="admin-course-list-page:add-new-course:btn"
                    text={t("addNewCourse")}
                    onClick={() => setShowCourseModal(true)}
                    buttonSize={ButtonSize.Medium}
                    buttonStyle={ButtonStyle.TertiaryAlt}
                    iconType={Icons.Plus}
                />
            </HeaderBanner>
            <div className={`${CSS_CLASS_NAME}__window`}>
                <div className={`${CSS_CLASS_NAME}__list__view`}>
                    <div className={`${CSS_CLASS_NAME}__window__sidebar`}>
                        <div className={`${CSS_CLASS_NAME}__window__sidebar__filter`}>
                            <Paragraph
                                cssClassName={`${CSS_CLASS_NAME}__window__sidebar__filter__title`}
                                size={ParagraphSize.XLarge}
                                style={ParagraphStyle.Label}>
                                {t("filterResults")}
                            </Paragraph>
                            <div
                                className={`${CSS_CLASS_NAME}__window__sidebar__filter__selections`}>
                                <AdminCourseFilterComponent />
                            </div>
                        </div>
                    </div>
                    <div className={`${CSS_CLASS_NAME}__window__main`}>
                        <div className={`${CSS_CLASS_NAME}__window__main__courses`}>
                            <div className={`${CSS_CLASS_NAME}__window__main__courses__content`}>
                                <SkipNavContent>
                                    <CourseList courses={courses} isLoading={isLoading} />
                                </SkipNavContent>
                            </div>
                        </div>
                        {CollectionUtils.hasValues(courses) && (
                            <div className={`${CSS_CLASS_NAME}__window__main__pager`}>
                                <Pager
                                    currentPage={initialCriteria?.currentPage ?? 1}
                                    totalPages={totalNumberOfCourses / ITEMS_PER_PAGE}
                                    onPageClick={onPageClick}
                                    itemsPerPage={ITEMS_PER_PAGE}
                                    totalItems={totalNumberOfCourses}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <CourseModal open={showCourseModal} setOpen={setShowCourseModal} />
        </>
    );
});

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { AdminCourseListPage };

// #endregion Exports
